import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { 
  Headphones, Send, MessageSquare, Package, CreditCard, HelpCircle,
  Clock, CheckCircle, ArrowLeft, Users, Plane
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const MessagesPage = () => {
  const { user, token, API } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightTicketId = searchParams.get('ticket');
  const highlightConvoId = searchParams.get('conversation');
  
  const [tickets, setTickets] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [convoMessage, setConvoMessage] = useState('');
  const [activeTab, setActiveTab] = useState(highlightConvoId ? 'conversations' : 'support');
  const scrollRef = useRef(null);
  
  const [formData, setFormData] = useState({
    subject: '', reference_id: '', message: ''
  });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (highlightTicketId && tickets.length > 0 && !selectedTicket) {
      const t = tickets.find(t => t.id === highlightTicketId);
      if (t) { setSelectedTicket(t); setActiveTab('support'); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightTicketId, tickets]);

  useEffect(() => {
    if (highlightConvoId && conversations.length > 0 && !selectedConvo) {
      const c = conversations.find(c => c.id === highlightConvoId);
      if (c) { setSelectedConvo(c); setActiveTab('conversations'); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightConvoId, conversations]);

  const fetchAll = async () => {
    try {
      const [ticketRes, convoRes] = await Promise.all([
        axios.get(`${API}/support/tickets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTickets(ticketRes.data);
      setConversations(convoRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Votre demande a été envoyée');
      setFormData({ subject: '', reference_id: '', message: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleTicketReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, 
        { message: replyMessage }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Réponse envoyée');
      setReplyMessage('');
      fetchAll();
      const res = await axios.get(`${API}/support/tickets/${selectedTicket.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleConvoReply = async () => {
    if (!convoMessage.trim() || !selectedConvo) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selectedConvo.id}/messages`, 
        { content: convoMessage }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Message envoyé');
      setConvoMessage('');
      fetchAll();
      const res = await axios.get(`${API}/conversations/${selectedConvo.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedConvo(res.data);
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selectedTicket, selectedConvo]);

  const getSubjectIcon = (s) => {
    if (s === 'order' || s === 'listing') return <Package className="h-4 w-4 text-primary" />;
    if (s === 'payment') return <CreditCard className="h-4 w-4 text-green-500" />;
    return <HelpCircle className="h-4 w-4 text-violet-500" />;
  };
  const getSubjectLabel = (s) => {
    const m = { order: 'Commande', listing: 'Annonce', payment: 'Paiement' };
    return m[s] || 'Autre';
  };
  const statusBadge = (s) => {
    if (s === 'open') return <Badge className="bg-yellow-500/20 text-yellow-500">En attente</Badge>;
    if (s === 'in_progress') return <Badge className="bg-primary/20 text-primary">En cours</Badge>;
    if (s === 'closed') return <Badge className="bg-green-500/20 text-green-500">Résolu</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };
  const fmt = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const openTicketsCount = tickets.filter(t => t.status !== 'closed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="messages-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-24">
        <div className="flex items-center gap-3 mb-8">
          <MessageSquare className="h-8 w-8 text-primary" />
          <div>
            <h1 className="font-heading text-3xl font-bold">Messagerie</h1>
            <p className="text-muted-foreground">Discussions et support</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="conversations" data-testid="tab-conversations">
              <Users className="w-4 h-4 mr-1" />
              Discussions ({conversations.length})
            </TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">
              <Headphones className="w-4 h-4 mr-1" />
              Support
              {openTicketsCount > 0 && (
                <Badge className="ml-2 bg-violet-500 text-white text-xs">{openTicketsCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* CONVERSATIONS TAB */}
          <TabsContent value="conversations">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Conversation List */}
              <Card className="card-dashboard lg:col-span-1">
                <CardHeader className="border-b pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" /> Conversations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    {conversations.length === 0 ? (
                      <div className="p-8 text-center">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground text-sm">Aucune conversation</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Utilisez "Contacter le vendeur" sur une commande
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {conversations.map((convo) => {
                          const otherName = convo.participant_1_id === user?.id ? convo.participant_2_name : convo.participant_1_name;
                          const lastMsg = convo.messages?.[convo.messages.length - 1];
                          return (
                            <div 
                              key={convo.id}
                              className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${selectedConvo?.id === convo.id ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                              onClick={() => setSelectedConvo(convo)}
                              data-testid={`convo-item-${convo.id}`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Plane className="h-4 w-4 text-primary" />
                                <span className="font-medium text-sm truncate">{otherName}</span>
                              </div>
                              {convo.route && (
                                <p className="text-xs text-muted-foreground mb-1">{convo.route}</p>
                              )}
                              {lastMsg && (
                                <p className="text-xs text-muted-foreground line-clamp-1">{lastMsg.content}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Clock className="h-3 w-3" />{fmt(convo.updated_at)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Conversation Detail */}
              <Card className="card-dashboard lg:col-span-2">
                <CardContent className="p-0">
                  {selectedConvo ? (
                    <div className="flex flex-col h-[560px]">
                      <div className="p-4 border-b flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedConvo(null)} className="lg:hidden">
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <div>
                            <h4 className="font-medium">
                              {selectedConvo.participant_1_id === user?.id ? selectedConvo.participant_2_name : selectedConvo.participant_1_name}
                            </h4>
                            <p className="text-xs text-muted-foreground">{selectedConvo.route}</p>
                          </div>
                        </div>
                        {selectedConvo.order_id && (
                          <Badge variant="outline" className="text-xs">
                            Commande #{selectedConvo.order_id.slice(0,8).toUpperCase()}
                          </Badge>
                        )}
                      </div>
                      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                        <div className="space-y-3">
                          {selectedConvo.messages?.map((msg) => (
                            <div key={msg.id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                                msg.sender_id === user?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                              }`}>
                                {msg.sender_id !== user?.id && (
                                  <p className="text-xs font-medium opacity-70 mb-0.5">{msg.sender_name}</p>
                                )}
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'opacity-60' : 'text-muted-foreground'}`}>
                                  {fmt(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      <div className="p-3 border-t flex gap-2">
                        <Textarea 
                          value={convoMessage}
                          onChange={(e) => setConvoMessage(e.target.value)}
                          placeholder="Votre message..."
                          rows={1}
                          className="flex-1 resize-none"
                          data-testid="convo-reply-input"
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleConvoReply(); } }}
                        />
                        <Button onClick={handleConvoReply} disabled={sending || !convoMessage.trim()} data-testid="convo-reply-btn">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-16 text-center">
                      <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                      <p className="text-muted-foreground">Sélectionnez une conversation</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SUPPORT TAB */}
          <TabsContent value="support">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* New Ticket Form */}
              <Card className="card-dashboard lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Headphones className="h-5 w-5" /> Contacter le support
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitTicket} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Sujet *</Label>
                      <Select value={formData.subject} onValueChange={(v) => setFormData(p => ({ ...p, subject: v }))}>
                        <SelectTrigger data-testid="support-subject-select">
                          <SelectValue placeholder="Sélectionnez un sujet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="order">Question sur une commande</SelectItem>
                          <SelectItem value="listing">Question sur une annonce</SelectItem>
                          <SelectItem value="payment">Question sur un paiement</SelectItem>
                          <SelectItem value="other">Autre question</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(formData.subject === 'order' || formData.subject === 'listing') && (
                      <div className="space-y-2">
                        <Label>ID de référence (optionnel)</Label>
                        <Input value={formData.reference_id} onChange={(e) => setFormData(p => ({ ...p, reference_id: e.target.value }))} placeholder="Ex: abc123..." data-testid="support-reference-input" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Votre message *</Label>
                      <Textarea value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))} placeholder="Décrivez votre problème..." rows={4} required data-testid="support-message-input" />
                    </div>
                    <Button type="submit" className="w-full gap-2" disabled={sending} data-testid="support-submit-btn">
                      {sending ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="h-4 w-4" />}
                      Envoyer
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Ticket List / Detail */}
              <Card className="card-dashboard lg:col-span-2">
                <CardHeader className="border-b">
                  <CardTitle className="text-lg">
                    {selectedTicket ? (
                      <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)} className="gap-2">
                        <ArrowLeft className="h-4 w-4" />Retour
                      </Button>
                    ) : `Mes demandes (${tickets.length})`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {selectedTicket ? (
                    <div className="p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getSubjectIcon(selectedTicket.subject)}
                          <div>
                            <h4 className="font-medium">{getSubjectLabel(selectedTicket.subject)}</h4>
                            {selectedTicket.reference_id && (
                              <p className="text-sm text-muted-foreground">Réf: {selectedTicket.reference_id}</p>
                            )}
                          </div>
                        </div>
                        {statusBadge(selectedTicket.status)}
                      </div>
                      <ScrollArea className="h-[300px] border rounded-lg p-4">
                        <div className="space-y-4">
                          {/* Messages from the messages array */}
                          {selectedTicket.messages?.map((msg, idx) => (
                            <div key={msg.id || idx} className={`flex ${msg.sender === 'admin' || msg.sender_id !== user?.id ? 'justify-start' : 'justify-end'}`}>
                              <div className={`max-w-[80%] rounded-lg p-3 ${
                                msg.sender === 'admin' || (msg.sender_id && msg.sender_id !== user?.id)
                                  ? 'bg-primary/10 border border-primary/20'
                                  : 'bg-muted'
                              }`}>
                                {(msg.sender === 'admin' || (msg.sender_id && msg.sender_id !== user?.id)) && (
                                  <p className="text-xs font-medium text-primary mb-1">
                                    {msg.sender_name || 'Support Kacos'}
                                  </p>
                                )}
                                <p className="text-sm">{msg.content}</p>
                                <p className="text-xs text-muted-foreground mt-1">{fmt(msg.created_at)}</p>
                              </div>
                            </div>
                          ))}
                          {/* Legacy replies */}
                          {selectedTicket.replies?.map((reply, idx) => (
                            <div key={`reply-${idx}`} className={`flex ${reply.sender_role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                              <div className={`max-w-[80%] rounded-lg p-3 ${
                                reply.sender_role === 'admin' ? 'bg-primary/10 border border-primary/20' : 'bg-muted'
                              }`}>
                                {reply.sender_role === 'admin' && (
                                  <p className="text-xs font-medium text-primary mb-1">Support Kacos</p>
                                )}
                                <p className="text-sm">{reply.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">{fmt(reply.created_at)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      {selectedTicket.status !== 'closed' ? (
                        <div className="flex gap-2">
                          <Textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} placeholder="Votre réponse..." rows={2} className="flex-1" data-testid="ticket-reply-input" />
                          <Button onClick={handleTicketReply} disabled={sending || !replyMessage.trim()} data-testid="ticket-reply-btn">
                            {sending ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="h-4 w-4" />}
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-2 text-sm text-muted-foreground bg-muted/50 rounded-lg">
                          <CheckCircle className="h-4 w-4 inline mr-2" />Ce ticket est résolu
                        </div>
                      )}
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      {tickets.length === 0 ? (
                        <div className="p-8 text-center">
                          <Headphones className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">Aucune demande de support</p>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {tickets.map((ticket) => (
                            <div key={ticket.id} className="p-4 hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => setSelectedTicket(ticket)} data-testid={`ticket-item-${ticket.id}`}>
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                  {getSubjectIcon(ticket.subject)}
                                  <div>
                                    <p className="font-medium text-sm">
                                      {getSubjectLabel(ticket.subject)}
                                      {ticket.reference_id && <span className="text-muted-foreground ml-2">#{ticket.reference_id.slice(0,8)}</span>}
                                    </p>
                                    <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{ticket.message}</p>
                                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                      <Clock className="h-3 w-3" />{fmt(ticket.created_at)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  {statusBadge(ticket.status)}
                                  {(ticket.replies?.length > 0 || ticket.messages?.length > 1) && (
                                    <Badge variant="outline" className="text-xs">
                                      {(ticket.replies?.length || 0) + Math.max(0, (ticket.messages?.length || 1) - 1)} réponse(s)
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MessagesPage;
